import type { IParkingStats } from '@nuxt-monorepo/ui/types/ParkingStats';
import type { IAirportProducts, IAirportStats, IHotelsSummary, IParkingSummary } from '@nuxt-monorepo/ui/types';

export function getProductsFromResponse(psf: any): IAirportProducts {
    let hotels: any | null;
    let parking: any | null;
    if (psf.data?.value?.data?.providers) {
        hotels = psf.data?.value?.data?.providers?.Psf ?? null;
        parking = psf.data?.value?.data?.providers?.Apr ?? null;
    } else {
        hotels = psf.data?.value?.data?.psf?.hotels ?? null;
        parking = psf.data?.value?.data?.parking?.parking_lots ?? null;
    }
    const parkingStats: IParkingStats | null = psf.data?.value?.data?.parkinglots_stats ?? null;
    const airportStats: any = psf.data?.value?.data?.psf?.airport_stats
        ? psf.data?.value?.data?.psf.airport_stats
        : psf.data?.value?.data?.airport_stats;

    if (hotels !== null) {
        hotels = ensureArrayOfObjects(hotels);
    }

    if (parking !== null) {
        parking = ensureArrayOfObjects(parking);
    }

    // Only show parkinglots in service
    parking = parking
        ?.map((p) => {
            if (p.surveys) delete p.surveys;
            return p;
        })
        ?.filter((e) => {
            if (e.service === false) return false;
            return true;
        });

    return { parking, hotels, parkingStats, airportStats };
}

export const getProductHotelsSummary = (products: any): IHotelsSummary => {
    let hotels: any | null;
    if (products.data?.value?.data?.providers) {
        hotels = products.data?.value?.data?.providers?.Psf ?? null;
    } else {
        hotels = products.data?.value?.data?.psf?.hotels ?? null;
    }
    if (hotels !== null) {
        hotels = ensureArrayOfObjects(hotels);
    }

    return {
        code: products.data?.value?.data?.content.content.code,
        totalHotels: hotels?.length,
        mostPopularHotel: hotels?.[0]?.name,
    };
};

export const getPortParkingSummary = (products: any): IParkingSummary => {
    let parking: any | null;
    if (products.data?.value?.data?.providers) {
        parking = products.data?.value?.data?.providers?.Apr ?? null;
    } else {
        parking = products.data?.value?.data?.parking?.parking_lots ?? null;
    }

    if (parking !== null) {
        parking = ensureArrayOfObjects(parking);
    }

    let closest = '';

    if (parking) {
        closest = parking.reduce((res: any, product: any) => {
            return res && parseFloat(product.distance) > parseFloat(res.distance) ? res : product;
        }, null)?.name;
    }

    // filter only parkings with service === true
    // pick one with the highest rating + rating_count
    let bestRated: any;
    if (parking?.length) {
        const sorted = parking
            .filter((product) => product.service)
            .sort((a, b) => b.ratings.rating_count - a.ratings.rating_count || b.ratings.rating - a.ratings.rating);
        bestRated = sorted?.length > 0 ? sorted[0].name : undefined;
    }

    return {
        code: products.data?.value?.data?.airport.code,
        totalLots: parking?.length,
        bestRatedLot: bestRated,
        mostBookedLot: parking?.[0]?.name,
        closestToAirportLot: closest,
    };
};

export const getCouponsSummary = (products: any): any => {
    let parking: any | null;
    if (products.data?.value?.data?.providers) {
        parking = products.data?.value?.data?.providers?.Apr ?? null;
    } else {
        parking = products.data?.value?.data?.parking?.parking_lots ?? null;
    }

    if (parking !== null) {
        parking = ensureArrayOfObjects(parking);
    }

    const valid = parking?.filter((product: any) => product.service)?.length;

    return {
        code: products.data?.value?.data.airport.code,
        coupon: products.data?.value?.data?.coupon,
        valid,
    };
};

export const getAirports = (data: any) => {
    let airports: any | null;
    airports = data.value?.data?.airports ?? null;

    if (airports !== null) {
        airports = ensureArrayOfObjects(airports);
    }

    return airports;
};

export function ensureArrayOfObjects(value: any) {
    if (Array.isArray(value)) {
        return value;
    }
    return Object.values(value);
}
